import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ChevronDownIcon,
  ChevronRightIcon,
  TrophyIcon,
  DocumentArrowDownIcon,
  ArrowTopRightOnSquareIcon,
  BookOpenIcon,
  TagIcon,
  IdentificationIcon,
} from "@heroicons/react/24/outline";
import RankingsTab from "./reports/RankingsTab";
import PageHeader from "../components/layout/PageHeader";
import { Badge } from "../components/ui/Badge";
import toast from "react-hot-toast";
import { reportsApi } from "../api/reports";
import { useSeason } from "../context/SeasonContext";
import { useAuth } from "../hooks/useAuth";
import { useSessionTotals } from "../hooks/useSessionTotals";
import { formatSessionTotalsLine } from "../utils/sessionTotals";
import { formatCabinNumber, normalizeDivisionName } from "../utils/terminology";
import { isCamperCabin } from "../utils/cabinFilter";
import type {
  IndividualSummaryRow,
  CabinSummaryRow,
  ActivitySummaryRow,
  TeamReportData,
} from "../types";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function Spinner() {
  return (
    <div className="flex items-center justify-center py-16">
      <div className="w-6 h-6 border-2 border-forest-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

function pct(n: number, d: number) {
  return d > 0 ? Math.round((n / d) * 100) : 0;
}

// ─── Shared controls bar ──────────────────────────────────────────────────────

interface ControlsProps {
  showLifetime: boolean;
  onShowLifetimeChange: (v: boolean) => void;
}

function Controls({ showLifetime, onShowLifetimeChange }: ControlsProps) {
  return (
    <div className="flex flex-wrap items-center gap-4 mb-6">
      <label className="flex items-center gap-2 text-sm text-stone-600 cursor-pointer select-none">
        <input
          type="checkbox"
          checked={showLifetime}
          onChange={(e) => onShowLifetimeChange(e.target.checked)}
          className="rounded border-stone-300 text-forest-600 focus:ring-forest-500"
        />
        Show lifetime totals
      </label>

      <div className="ml-auto flex items-center gap-4">
        <Link
          to="/reports/kerchief"
          className="flex items-center gap-1.5 text-sm text-forest-700 hover:text-forest-900 font-medium"
        >
          <TrophyIcon className="w-4 h-4" />
          Kerchief Report
          <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5" />
        </Link>
        <Link
          to="/reports/kerchief-ceremony"
          className="flex items-center gap-1.5 text-sm text-forest-700 hover:text-forest-900 font-medium"
        >
          <DocumentArrowDownIcon className="w-4 h-4" />
          Ceremony Report
          <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5" />
        </Link>
        <Link
          to="/reports/ac-books"
          className="flex items-center gap-1.5 text-sm text-forest-700 hover:text-forest-900 font-medium"
        >
          <BookOpenIcon className="w-4 h-4" />
          AC Books
          <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5" />
        </Link>
        <Link
          to="/reports/camper-summary"
          className="flex items-center gap-1.5 text-sm text-forest-700 hover:text-forest-900 font-medium"
        >
          <IdentificationIcon className="w-4 h-4" />
          Camper Summary
          <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5" />
        </Link>
        <Link
          to="/reports/kerchief-stamping"
          className="flex items-center gap-1.5 text-sm text-forest-700 hover:text-forest-900 font-medium"
        >
          <TagIcon className="w-4 h-4" />
          Kerchief Stamping
          <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5" />
        </Link>
      </div>
    </div>
  );
}

// ─── Individual Tab ───────────────────────────────────────────────────────────

function IndividualTab({ termId, year, termLabel, showLifetime }: { termId: number | null; year: number; termLabel: string; showLifetime: boolean }) {
  const [rows, setRows]           = useState<IndividualSummaryRow[]>([]);
  const [loading, setLoading]     = useState(true);
  const [search, setSearch]       = useState("");
  const [expanded, setExpanded]   = useState<Set<number>>(new Set());
  const [sortKey, setSortKey]     = useState<keyof IndividualSummaryRow>("yearACs");
  const [sortDir, setSortDir]     = useState<"asc" | "desc">("desc");

  useEffect(() => {
    setLoading(true);
    reportsApi
      .individualSummary(termId, year)
      .then(setRows)
      .catch(() => toast.error("Failed to load individual summary"))
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [termId, year]);

  const filtered = rows.filter((r) =>
    `${r.camper.firstName} ${r.camper.lastName}`.toLowerCase().includes(search.toLowerCase())
  );

  const sorted = [...filtered].sort((a, b) => {
    const av = a[sortKey] as number;
    const bv = b[sortKey] as number;
    return sortDir === "desc" ? bv - av : av - bv;
  });

  function toggleSort(key: keyof IndividualSummaryRow) {
    if (sortKey === key) {
      setSortDir((d) => (d === "desc" ? "asc" : "desc"));
    } else {
      setSortKey(key);
      setSortDir("desc");
    }
  }

  function toggleExpand(id: number) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function SortHeader({ label, field }: { label: string; field: keyof IndividualSummaryRow }) {
    const active = sortKey === field;
    return (
      <th
        className="px-4 py-3 text-right text-xs font-semibold text-stone-500 uppercase tracking-wide cursor-pointer select-none hover:text-stone-700 whitespace-nowrap"
        onClick={() => toggleSort(field)}
      >
        {label} {active ? (sortDir === "desc" ? "↓" : "↑") : ""}
      </th>
    );
  }

  if (loading) return <Spinner />;

  return (
    <div>
      <div className="mb-4">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search campers…"
          className="block w-64 rounded-md border border-stone-300 text-sm px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-forest-500"
        />
      </div>

      {sorted.length === 0 ? (
        <p className="text-center text-stone-400 py-12 text-sm">
          No campers enrolled in {termLabel || "this term"}.
        </p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-stone-200">
          <table className="min-w-full divide-y divide-stone-200 text-sm">
            <thead className="bg-stone-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-stone-500 uppercase tracking-wide w-8" />
                <th className="px-4 py-3 text-left text-xs font-semibold text-stone-500 uppercase tracking-wide">Camper</th>
                <SortHeader label="ACs (term)" field="yearACs" />
                {showLifetime && <SortHeader label="ACs (lifetime)" field="lifetimeACs" />}
                <SortHeader label="Honors (term)" field="yearHonors" />
                {showLifetime && <SortHeader label="Honors (lifetime)" field="lifetimeHonors" />}
                <SortHeader label="Graduated (term)" field="yearGraduations" />
                {showLifetime && <SortHeader label="Graduated (lifetime)" field="lifetimeGraduations" />}
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100 bg-white">
              {sorted.map((row) => {
                const isOpen = expanded.has(row.camper.id);
                return (
                  <>
                    <tr
                      key={row.camper.id}
                      className="hover:bg-stone-50 cursor-pointer"
                      onClick={() => toggleExpand(row.camper.id)}
                    >
                      <td className="px-4 py-3 text-stone-400">
                        {isOpen
                          ? <ChevronDownIcon className="w-4 h-4" />
                          : <ChevronRightIcon className="w-4 h-4" />}
                      </td>
                      <td className="px-4 py-3 font-medium text-stone-800">
                        <span>{row.camper.firstName} {row.camper.lastName}</span>
                        {row.isFullTerm && (
                          <span className="ml-2 inline-block px-1.5 py-0.5 rounded text-xs font-semibold bg-forest-100 text-forest-800">
                            Full Term
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums text-stone-700">{row.yearACs}</td>
                      {showLifetime && (
                        <td className="px-4 py-3 text-right tabular-nums text-stone-400">{row.lifetimeACs}</td>
                      )}
                      <td className="px-4 py-3 text-right tabular-nums text-stone-700">{row.yearHonors}</td>
                      {showLifetime && (
                        <td className="px-4 py-3 text-right tabular-nums text-stone-400">{row.lifetimeHonors}</td>
                      )}
                      <td className="px-4 py-3 text-right tabular-nums text-stone-700">{row.yearGraduations}</td>
                      {showLifetime && (
                        <td className="px-4 py-3 text-right tabular-nums text-stone-400">{row.lifetimeGraduations}</td>
                      )}
                    </tr>
                    {isOpen && (
                      <tr key={`${row.camper.id}-detail`} className="bg-stone-50">
                        <td />
                        <td colSpan={showLifetime ? 6 : 3} className="px-6 pb-4 pt-2">
                          <p className="text-xs font-semibold text-stone-400 uppercase tracking-wide mb-2">
                            Activity Breakdown
                          </p>
                          {row.activityBreakdown.length === 0 ? (
                            <p className="text-stone-400 text-xs">No activity data.</p>
                          ) : (
                            <table className="text-xs w-full">
                              <thead>
                                <tr className="text-stone-400">
                                  <th className="text-left py-1 pr-4 font-medium">Activity</th>
                                  <th className="text-right py-1 pr-4 font-medium">ACs (term)</th>
                                  <th className="text-right py-1 pr-4 font-medium">Total ACs</th>
                                  <th className="text-right py-1 pr-4 font-medium">Honor</th>
                                  <th className="text-right py-1 font-medium">Graduated</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-stone-100">
                                {row.activityBreakdown.map((ab) => (
                                  <tr key={ab.activityId} className="text-stone-700">
                                    <td className="py-1.5 pr-4 font-medium">{ab.activityName}</td>
                                    <td className="py-1.5 pr-4 text-right tabular-nums">{ab.yearACs}</td>
                                    <td className="py-1.5 pr-4 text-right tabular-nums">{ab.totalACs}</td>
                                    <td className="py-1.5 pr-4 text-right">
                                      {ab.honorCount > 0 ? (
                                        <span className="inline-block px-1.5 py-0.5 rounded text-amber-800 bg-amber-100 font-semibold text-xs">
                                          {ab.honorCount > 1 ? `${ab.honorCount}× H` : "H"}
                                        </span>
                                      ) : "—"}
                                    </td>
                                    <td className="py-1.5 text-right">
                                      {ab.graduated ? (
                                        <span className="inline-block px-1.5 py-0.5 rounded text-forest-800 bg-forest-100 font-semibold text-xs">✓</span>
                                      ) : "—"}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                        </td>
                      </tr>
                    )}
                  </>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Cabin Tab ────────────────────────────────────────────────────────────────

function CabinTab({ termId, year, termLabel, showLifetime }: { termId: number | null; year: number; termLabel: string; showLifetime: boolean }) {
  const [rows, setRows]         = useState<CabinSummaryRow[]>([]);
  const [loading, setLoading]   = useState(true);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  useEffect(() => {
    setLoading(true);
    reportsApi
      .cabinSummary(termId, year)
      .then(setRows)
      .catch(() => toast.error("Failed to load cabin summary"))
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [termId, year]);

  function toggleExpand(key: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  if (loading) return <Spinner />;

  return (
    <div>
      {rows.length === 0 ? (
        <p className="text-center text-stone-400 py-12 text-sm">No cabin data for {termLabel || "this term"}.</p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-stone-200">
          <table className="min-w-full divide-y divide-stone-200 text-sm">
            <thead className="bg-stone-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-stone-500 uppercase tracking-wide w-8" />
                <th className="px-4 py-3 text-left text-xs font-semibold text-stone-500 uppercase tracking-wide">Cabin</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-stone-500 uppercase tracking-wide">Division</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-stone-500 uppercase tracking-wide">Campers</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-stone-500 uppercase tracking-wide whitespace-nowrap">Total ACs</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-stone-500 uppercase tracking-wide whitespace-nowrap">Total Honors</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-stone-500 uppercase tracking-wide whitespace-nowrap">Total Graduations</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100 bg-white">
              {rows.filter((row) => isCamperCabin({ name: row.cabin.name })).map((row) => {
                const cabinKey = String(row.cabin.id ?? row.cabin.name);
                const isOpen = expanded.has(cabinKey);
                const hasCampers = row.campers && row.campers.length > 0;
                const cabinDisplay = formatCabinNumber(row.cabin.name);
                return (
                  <>
                    <tr
                      key={cabinKey}
                      className={hasCampers ? "hover:bg-stone-50 cursor-pointer" : ""}
                      onClick={() => hasCampers && toggleExpand(cabinKey)}
                    >
                      <td className="px-4 py-3 text-stone-400">
                        {hasCampers && (isOpen
                          ? <ChevronDownIcon className="w-4 h-4" />
                          : <ChevronRightIcon className="w-4 h-4" />)}
                      </td>
                      <td className="px-4 py-3 font-medium text-stone-800">{cabinDisplay}</td>
                      <td className="px-4 py-3 text-stone-500">{row.cabin.divisionName ? normalizeDivisionName(row.cabin.divisionName) : "—"}</td>
                      <td className="px-4 py-3 text-right tabular-nums text-stone-700">{row.camperCount}</td>
                      <td className="px-4 py-3 text-right tabular-nums text-stone-700">{row.totalACs}</td>
                      <td className="px-4 py-3 text-right tabular-nums text-stone-700">{row.totalHonors}</td>
                      <td className="px-4 py-3 text-right tabular-nums text-stone-700">{row.totalGraduations}</td>
                    </tr>
                    {isOpen && hasCampers && (
                      <tr key={`${cabinKey}-detail`} className="bg-stone-50">
                        <td />
                        <td colSpan={6} className="px-6 pb-4 pt-2">
                          <p className="text-xs font-semibold text-stone-400 uppercase tracking-wide mb-2">
                            Campers in {cabinDisplay}
                          </p>
                          <table className="text-xs w-full">
                            <thead>
                              <tr className="text-stone-400">
                                <th className="text-left py-1 pr-4 font-medium">Camper</th>
                                <th className="text-right py-1 pr-4 font-medium">ACs (term)</th>
                                {showLifetime && <th className="text-right py-1 pr-4 font-medium">ACs (lifetime)</th>}
                                <th className="text-right py-1 pr-4 font-medium">Honors (term)</th>
                                {showLifetime && <th className="text-right py-1 pr-4 font-medium">Honors (lifetime)</th>}
                                <th className="text-right py-1 font-medium">Graduated (term)</th>
                                {showLifetime && <th className="text-right py-1 font-medium">Graduated (lifetime)</th>}
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-stone-100">
                              {row.campers!.map((cr) => (
                                <tr key={cr.camper.id} className="text-stone-700">
                                  <td className="py-1.5 pr-4 font-medium">
                                    {cr.camper.firstName} {cr.camper.lastName}
                                  </td>
                                  <td className="py-1.5 pr-4 text-right tabular-nums">{cr.yearACs}</td>
                                  {showLifetime && <td className="py-1.5 pr-4 text-right tabular-nums text-stone-400">{cr.lifetimeACs}</td>}
                                  <td className="py-1.5 pr-4 text-right tabular-nums">{cr.yearHonors}</td>
                                  {showLifetime && <td className="py-1.5 pr-4 text-right tabular-nums text-stone-400">{cr.lifetimeHonors}</td>}
                                  <td className="py-1.5 text-right tabular-nums">{cr.yearGraduations}</td>
                                  {showLifetime && <td className="py-1.5 text-right tabular-nums text-stone-400">{cr.lifetimeGraduations}</td>}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    )}
                  </>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Activity Tab ─────────────────────────────────────────────────────────────

function ActivityTab({ termId, year, termLabel, showLifetime }: { termId: number | null; year: number; termLabel: string; showLifetime: boolean }) {
  const [rows, setRows]       = useState<ActivitySummaryRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  useEffect(() => {
    setLoading(true);
    reportsApi
      .activitySummary(termId, year, showLifetime)
      .then(setRows)
      .catch(() => toast.error("Failed to load activity summary"))
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [termId, year, showLifetime]);

  function toggleExpand(id: number) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  if (loading) return <Spinner />;

  return (
    <div>
      {rows.length === 0 ? (
        <p className="text-center text-stone-400 py-12 text-sm">No activity data for {termLabel || "this term"}.</p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-stone-200">
          <table className="min-w-full divide-y divide-stone-200 text-sm">
            <thead className="bg-stone-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-stone-500 uppercase tracking-wide w-8" />
                <th className="px-4 py-3 text-left text-xs font-semibold text-stone-500 uppercase tracking-wide">Activity</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-stone-500 uppercase tracking-wide">Enrolled</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-stone-500 uppercase tracking-wide">Honors</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-stone-500 uppercase tracking-wide">Graduated</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-stone-500 uppercase tracking-wide">Grad Rate</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100 bg-white">
              {rows.map((row) => {
                const isOpen = expanded.has(row.activity.id);
                return (
                  <>
                    <tr
                      key={row.activity.id}
                      className="hover:bg-stone-50 cursor-pointer"
                      onClick={() => toggleExpand(row.activity.id)}
                    >
                      <td className="px-4 py-3 text-stone-400">
                        {isOpen
                          ? <ChevronDownIcon className="w-4 h-4" />
                          : <ChevronRightIcon className="w-4 h-4" />}
                      </td>
                      <td className="px-4 py-3 font-medium text-stone-800">{row.activity.name}</td>
                      <td className="px-4 py-3 text-right tabular-nums text-stone-700">{row.enrolledCount}</td>
                      <td className="px-4 py-3 text-right tabular-nums text-stone-700">{row.honorCount}</td>
                      <td className="px-4 py-3 text-right tabular-nums text-stone-700">{row.graduatedCount}</td>
                      <td className="px-4 py-3 text-right">
                        <span
                          className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${
                            row.graduationRate >= 75
                              ? "bg-forest-100 text-forest-800"
                              : row.graduationRate >= 40
                              ? "bg-amber-100 text-amber-800"
                              : "bg-stone-100 text-stone-600"
                          }`}
                        >
                          {row.graduationRate}%
                        </span>
                      </td>
                    </tr>
                    {isOpen && (
                      <tr key={`${row.activity.id}-detail`} className="bg-stone-50">
                        <td />
                        <td colSpan={6} className="px-6 pb-4 pt-2">
                          <p className="text-xs font-semibold text-stone-400 uppercase tracking-wide mb-2">
                            AC Breakdown — {row.activity.name}
                          </p>
                          {row.acBreakdown.length === 0 ? (
                            <p className="text-stone-400 text-xs">No ACs defined.</p>
                          ) : (
                            <table className="text-xs w-full max-w-lg">
                              <thead>
                                <tr className="text-stone-400">
                                  <th className="text-left py-1 pr-4 font-medium">AC</th>
                                  <th className="text-right py-1 pr-4 font-medium">Completed</th>
                                  <th className="text-right py-1 font-medium">Rate</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-stone-100">
                                {[...row.acBreakdown].sort((a, b) => a.acDisplayNumber - b.acDisplayNumber).map((ab) => (
                                  <tr key={ab.acId} className="text-stone-700">
                                    <td className="py-1.5 pr-4 font-medium">{ab.acDisplayNumber}. {ab.acName}</td>
                                    <td className="py-1.5 pr-4 text-right tabular-nums">{ab.completedCount}</td>
                                    <td className="py-1.5 text-right">
                                      <div className="flex items-center justify-end gap-2">
                                        <div className="w-16 bg-stone-200 rounded-full h-1.5">
                                          <div
                                            className="bg-forest-500 h-1.5 rounded-full"
                                            style={{ width: `${ab.completionRate}%` }}
                                          />
                                        </div>
                                        <span className="tabular-nums w-8 text-right">{ab.completionRate}%</span>
                                      </div>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                        </td>
                      </tr>
                    )}
                  </>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Team Tab ─────────────────────────────────────────────────────────────────

function TeamStatCard({ label, count, color, to }: {
  label: string;
  count: number;
  color: "army" | "navy" | "stone";
  to?: string;
}) {
  const styles = {
    army:  "bg-army-50 border-army-200 text-army-800",
    navy:  "bg-navy-50 border-navy-200 text-navy-800",
    stone: "bg-stone-50 border-stone-200 text-stone-600",
  };
  const inner = (
    <div className={`rounded-lg border p-4 flex flex-col gap-1 ${styles[color]} ${to ? "hover:opacity-80 transition-opacity cursor-pointer" : ""}`}>
      <span className="text-sm font-medium">{label}</span>
      <span className="text-3xl font-bold tabular-nums">{count}</span>
    </div>
  );
  if (to) return <Link to={to}>{inner}</Link>;
  return inner;
}

function TeamCountCells({ army, navy, unassigned, total }: { army: number; navy: number; unassigned: number; total: number }) {
  return (
    <>
      <td className="px-4 py-3 text-right tabular-nums">
        <Badge label={String(army)} color="army" />
      </td>
      <td className="px-4 py-3 text-right tabular-nums">
        <Badge label={String(navy)} color="navy" />
      </td>
      <td className="px-4 py-3 text-right tabular-nums text-stone-500">{unassigned}</td>
      <td className="px-4 py-3 text-right tabular-nums font-semibold text-stone-800">{total}</td>
    </>
  );
}

function TeamTab({ termId, year, termLabel }: { termId: number | null; year: number; termLabel: string }) {
  const { user } = useAuth();
  const canAssign = user?.role === "admin" || user?.role === "director";
  const [data, setData]       = useState<TeamReportData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    reportsApi
      .teamSummary(termId, year)
      .then(setData)
      .catch(() => toast.error("Failed to load team summary"))
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [termId, year]);

  if (loading) return <Spinner />;
  if (!data) return null;

  const teamHeader = (
    <tr>
      <th className="px-4 py-3 text-left text-xs font-semibold text-stone-500 uppercase tracking-wide" />
      <th className="px-4 py-3 text-right text-xs font-semibold text-army-700 uppercase tracking-wide">Army (A)</th>
      <th className="px-4 py-3 text-right text-xs font-semibold text-navy-700 uppercase tracking-wide">Navy (N)</th>
      <th className="px-4 py-3 text-right text-xs font-semibold text-stone-500 uppercase tracking-wide">Unassigned</th>
      <th className="px-4 py-3 text-right text-xs font-semibold text-stone-500 uppercase tracking-wide">Total</th>
    </tr>
  );

  return (
    <div className="space-y-8">
      {/* Overall summary cards */}
      <div>
        <h3 className="text-sm font-semibold text-stone-500 uppercase tracking-wide mb-3">
          Overall — {termLabel}
        </h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <TeamStatCard label="Army (A)" count={data.overall.army}       color="army"  />
          <TeamStatCard label="Navy (N)" count={data.overall.navy}       color="navy"  />
          <TeamStatCard
            label="Unassigned"
            count={data.overall.unassigned}
            color="stone"
            to={canAssign ? "/admin/team-assignment" : undefined}
          />
          <TeamStatCard label="Total Campers" count={data.overall.total}   color="stone" />
        </div>
      </div>

      {/* Staff summary cards */}
      {data.staff && (
        <div>
          <h3 className="text-sm font-semibold text-stone-500 uppercase tracking-wide mb-3">
            Staff
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <TeamStatCard label="Army (A)"     count={data.staff.army}       color="army"  />
            <TeamStatCard label="Navy (N)"     count={data.staff.navy}       color="navy"  />
            <TeamStatCard
              label="Unassigned"
              count={data.staff.unassigned}
              color="stone"
              to={canAssign ? "/admin/team-assignment" : undefined}
            />
            <TeamStatCard label="Total Staff"  count={data.staff.total}      color="stone" />
          </div>
        </div>
      )}

      {/* By Division */}
      <div>
        <h3 className="text-sm font-semibold text-stone-500 uppercase tracking-wide mb-3">By Division</h3>
        {data.byDivision.length === 0 ? (
          <p className="text-stone-400 text-sm">No division data for {termLabel}.</p>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-stone-200">
            <table className="min-w-full divide-y divide-stone-200 text-sm">
              <thead className="bg-stone-50">{teamHeader}</thead>
              <tbody className="divide-y divide-stone-100 bg-white">
                {[...data.byDivision]
                  .sort((a, b) => {
                    const order = ["Cubs", "Colts", "Juniors", "Seniors", "Rangers"];
                    const ai = order.indexOf(normalizeDivisionName(a.division.name));
                    const bi = order.indexOf(normalizeDivisionName(b.division.name));
                    const aRank = ai === -1 ? order.length : ai;
                    const bRank = bi === -1 ? order.length : bi;
                    if (aRank !== bRank) return aRank - bRank;
                    return a.division.name.localeCompare(b.division.name);
                  })
                  .map((row) => (
                  <tr key={row.division.id} className="hover:bg-stone-50">
                    <td className="px-4 py-3 font-medium text-stone-800">{normalizeDivisionName(row.division.name)}</td>
                    <TeamCountCells army={row.army} navy={row.navy} unassigned={row.unassigned} total={row.total} />
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* By Cabin */}
      <div>
        <h3 className="text-sm font-semibold text-stone-500 uppercase tracking-wide mb-3">By Cabin</h3>
        {data.byCabin.length === 0 ? (
          <p className="text-stone-400 text-sm">No cabin data for {termLabel}.</p>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-stone-200">
            <table className="min-w-full divide-y divide-stone-200 text-sm">
              <thead className="bg-stone-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-stone-500 uppercase tracking-wide">Cabin</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-stone-500 uppercase tracking-wide">Division</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-army-700 uppercase tracking-wide">A</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-navy-700 uppercase tracking-wide">N</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-stone-500 uppercase tracking-wide">Unassigned</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-stone-500 uppercase tracking-wide">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100 bg-white">
                {data.byCabin.filter((row) => isCamperCabin({ name: row.cabin.name })).map((row) => (
                  <tr key={row.cabin.id} className="hover:bg-stone-50">
                    <td className="px-4 py-3 font-medium text-stone-800">{formatCabinNumber(row.cabin.name)}</td>
                    <td className="px-4 py-3 text-stone-500">{row.divisionName ? normalizeDivisionName(row.divisionName) : "—"}</td>
                    <TeamCountCells army={row.army} navy={row.navy} unassigned={row.unassigned} total={row.total} />
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

type Tab = "individual" | "cabin" | "activity" | "team" | "rankings";

export default function Reports() {
  const [activeTab, setActiveTab]       = useState<Tab>("individual");
  const [showLifetime, setShowLifetime] = useState(true);
  const { selectedTermId, selectedYear, selectedTerm, activeSession } = useSeason();

  const termLabel = selectedTerm
    ? `${selectedTerm.year} — ${selectedTerm.name}`
    : `${selectedYear} — All Campers`;

  const { totals: sessionTotals } = useSessionTotals({
    year: selectedYear,
    termId: selectedTermId,
    session: activeSession,
  });

  const tabs: { id: Tab; label: string }[] = [
    { id: "individual", label: "Individual" },
    { id: "cabin",      label: "Cabin" },
    { id: "activity",   label: "Activity" },
    { id: "team",       label: "Teams" },
    { id: "rankings",   label: "Rankings" },
  ];

  return (
    <div className="flex flex-col min-h-full">
      <PageHeader
        title="Reports"
        subtitle="AC, Honor, and Graduation summaries by individual, cabin, and activity"
      />

      <div className="flex-1 p-6 lg:p-8 max-w-7xl mx-auto w-full">
        {sessionTotals && sessionTotals.total > 0 && (
          <div className="mb-4 px-4 py-2 rounded-md bg-forest-50 border border-forest-100 text-sm text-forest-800">
            {formatSessionTotalsLine(activeSession, sessionTotals)}
          </div>
        )}
        <Controls
          showLifetime={showLifetime}
          onShowLifetimeChange={setShowLifetime}
        />

        {/* Tabs */}
        <div className="border-b border-stone-200 mb-6">
          <nav className="-mb-px flex gap-6">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? "border-forest-600 text-forest-700"
                    : "border-transparent text-stone-500 hover:text-stone-700 hover:border-stone-300"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab content */}
        {activeTab === "individual" && (
          <IndividualTab termId={selectedTermId} year={selectedYear} termLabel={termLabel} showLifetime={showLifetime} />
        )}
        {activeTab === "cabin" && (
          <CabinTab termId={selectedTermId} year={selectedYear} termLabel={termLabel} showLifetime={showLifetime} />
        )}
        {activeTab === "activity" && (
          <ActivityTab termId={selectedTermId} year={selectedYear} termLabel={termLabel} showLifetime={showLifetime} />
        )}
        {activeTab === "team" && (
          <TeamTab termId={selectedTermId} year={selectedYear} termLabel={termLabel} />
        )}
        {activeTab === "rankings" && (
          <RankingsTab termId={selectedTermId} termLabel={termLabel} />
        )}
      </div>
    </div>
  );
}
